/**
 * TW: Subscription Gift — automatic free-gift cart management.
 *
 * Fully independent from assets/mirai-threshold-gift.js: own config element
 * (#tw-subscription-gift-config), own cart line-item marker
 * (_tw_subscription_gift), own sync logic. Does not read or touch anything
 * belonging to that other feature.
 *
 * Eligibility (enabled, available, threshold, customer-tag exclusion) is
 * computed server-side in snippets/tw-subscription-gift.liquid and passed
 * in via the config JSON this file reads.
 */
(function () {
  'use strict';

  var MARKER = '_tw_subscription_gift';
  var CONFIG_ID = 'tw-subscription-gift-config';

  function isGiftItem(item) {
    return !!(item.properties && item.properties[MARKER] === 'v1');
  }

  function hasSubscriptionItem(cart) {
    return cart.items.some(function (item) {
      return !isGiftItem(item) && !!item.selling_plan_allocation;
    });
  }

  function getConfig() {
    var node = document.getElementById(CONFIG_ID);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (err) {
      return null;
    }
  }

  function decide(cart, config) {
    var giftLines = cart.items.filter(isGiftItem);
    var giftTotal = giftLines.reduce(function (sum, item) {
      return sum + item.final_line_price;
    }, 0);
    var subtotal = cart.total_price - giftTotal;
    var qualifies = Boolean(
      config.enabled &&
      config.available &&
      config.variantId &&
      config.threshold > 0 &&
      subtotal >= config.threshold &&
      hasSubscriptionItem(cart)
    );

    var updates = {};
    var kept = null;
    giftLines.forEach(function (item) {
      if (qualifies && !kept && String(item.variant_id) === String(config.variantId)) {
        kept = item;
        if (item.quantity !== 1) updates[item.key] = 1;
      } else {
        updates[item.key] = 0;
      }
    });

    return { updates: updates, shouldAdd: qualifies && !kept };
  }

  // The cart drawer here is the third-party "Opus" app, which manages its
  // own cart display state client-side and won't notice a cart change made
  // by this script's own fetch calls unless explicitly told. After any
  // change we make, hand Opus the fresh cart so its drawer updates without
  // needing a page refresh.
  function notifyOpus(cart) {
    if (typeof window.OpusSetCartData === 'function') {
      window.OpusSetCartData(cart);
    }
  }

  var running = false;
  var pending = false;

  async function sync() {
    if (running) {
      pending = true;
      return;
    }
    running = true;

    try {
      var config = getConfig();
      if (!config) return;

      var cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      var result = decide(cart, config);
      var changed = false;

      if (Object.keys(result.updates).length) {
        cart = await (
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: result.updates })
          })
        ).json();
        changed = true;
      }

      if (result.shouldAdd) {
        var properties = {};
        properties[MARKER] = 'v1';
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: config.variantId, quantity: 1, properties: properties }] })
        });
        cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
        changed = true;
      }

      if (changed) {
        notifyOpus(cart);
        document.dispatchEvent(new CustomEvent('tw:subscription-gift-updated', { detail: { cart: cart } }));
      }
    } catch (err) {
      console.warn('[tw-subscription-gift] sync failed', err);
    } finally {
      running = false;
      if (pending) {
        pending = false;
        sync();
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }

  // Dawn's native cart-update pubsub, when present.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, sync);
  }

  // Generic cart-update custom events some apps/themes dispatch, plus the
  // Opus cart drawer app's own events. Listening on both document and
  // window since different apps dispatch these on either target.
  ['cart:updated', 'cart:refresh', 'OpusCartChange', 'OpusOpenCartState'].forEach(function (name) {
    document.addEventListener(name, sync);
    window.addEventListener(name, sync);
  });

  function isCartMutationUrl(url) {
    if (!url) return false;
    try {
      var parsed = new URL(url, window.location.origin);
      return /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(parsed.pathname);
    } catch (err) {
      return false;
    }
  }

  // Catch cart mutations made via fetch from anywhere on the page (quick-add
  // buttons, apps, etc.) so the gift stays in sync even without an explicit
  // event being dispatched.
  var nativeFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    var request = nativeFetch(input, init);
    try {
      var url = typeof input === 'string' ? input : input && input.url;
      if (isCartMutationUrl(url)) {
        request.then(function () { sync(); }).catch(function () {});
      }
    } catch (err) {
      // Ignore — never let the gift sync break an unrelated fetch call.
    }
    return request;
  };

  // The Opus cart drawer (and some other apps) use XMLHttpRequest rather
  // than fetch for cart calls — patch that too, or those changes would
  // never be noticed.
  var nativeOpen = XMLHttpRequest.prototype.open;
  var nativeSend = XMLHttpRequest.prototype.send;
  var watchedRequests = typeof WeakSet === 'function' ? new WeakSet() : null;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (watchedRequests) {
      if (isCartMutationUrl(url)) {
        watchedRequests.add(this);
      } else {
        watchedRequests.delete(this);
      }
    }
    return nativeOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (watchedRequests && watchedRequests.has(this)) {
      this.addEventListener('loadend', sync, { once: true });
    }
    return nativeSend.apply(this, arguments);
  };

  window.TwSubscriptionGift = { sync: sync };
})();
